import { ZoomableImage } from "@coworker/apprestructured/src/shared/components/ImageViewer/ZoomableImage/ZoomableImage";
import ClickableButtonWrapper from "@coworker/apprestructured/src/shared/wrappers/ClickableButtonWrapper/ClickableButtonWrapper";
import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import { useFailedImageUrls } from "@coworker/apprestructured/src/task-management/hooks/useFailedImageUrls";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import { useCallback, useId, useMemo, useState } from "react";
import { useNotifications } from "../Notifications/NotificationsContext";
import styles from "./CommentImages.module.css";

type CommentImagesProps = {
  urls: string[] | Loading;
};
export const CommentImages = ({ urls }: CommentImagesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);
  const { markImageAsFailed, isImageFailed } = useFailedImageUrls();
  const { showToast } = useNotifications();

  const { t } = useTranslation();

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleImageClick = useCallback(
    (index: number, url: string) => {
      if (isImageFailed(url)) {
        showToast({ text: t("imageCouldNotBeLoadedString") });
        return;
      }

      setActiveSlideImageIndex(index);
      setShowModal(true);
    },
    [isImageFailed, showToast, t]
  );

  const id = useId();

  const carousel = useMemo(() => {
    return (
      <FixaOverflowCarousel id={id}>
        {isLoading(urls) ? (
          <FixaSkeleton
            width="75px"
            height="75px"
            className={styles["skeleton"]}
          />
        ) : (
          urls.map((url, index) => {
            const imageIsFailed = isImageFailed(url);
            const imageClassName = [
              styles["image"],
              imageIsFailed ? styles["image-fallback"] : "",
            ]
              .join(" ")
              .trim();
            return (
              <ClickableButtonWrapper
                key={url}
                className={styles["imageButtonClassName"]}
                onClick={() => handleImageClick(index, url)}
                aria-label={`Image ${index + 1} button`}
              >
                <FixaImage
                  src={url}
                  fallback={imageIsFailed}
                  alt={`Image ${index + 1}`}
                  className={imageClassName}
                  onError={() => markImageAsFailed(url)}
                />
              </ClickableButtonWrapper>
            );
          })
        )}
      </FixaOverflowCarousel>
    );
  }, [handleImageClick, id, isImageFailed, markImageAsFailed, urls]);

  const modal = useMemo(() => {
    if (isLoading(urls)) {
      return null;
    }
    return (
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
        <FixaTheatre
          header={<FixaModalHeader />}
          ariaLabel={t("imageCarouselString")}
        >
          <FixaModalBody>
            <FixaCarousel
              id={"images"}
              isSlideShow
              slideIndex={activeSlideImageIndex}
              ariaLabelLeftBtn={t("previousImageString")}
              ariaLabelRightBtn={t("nextImageString")}
              className={styles["carousel"]}
            >
              {urls.map((url, index) => (
                <ZoomableImage key={url} src={url} alt={`Image ${index + 1}`} />
              ))}
            </FixaCarousel>
          </FixaModalBody>
        </FixaTheatre>
      </FixaModal>
    );
  }, [activeSlideImageIndex, handleCloseModal, urls, showModal, t]);

  if (!isLoading(urls) && urls.length === 0) {
    return null;
  }

  return (
    <>
      <div>{carousel}</div>
      {modal}
    </>
  );
};
